import type { KeyboardEvent } from "react";
import type { ParsedMissionStep } from "../../utils/missionTrace";
import {
  formatDateTime,
  formatMs,
  formatModelDisplay,
  KIND_LABELS,
  resolvedModelId,
  stepEndIso,
  timelineSteps,
} from "../../utils/missionTrace";
import { formatUsdMicros } from "@vida/currents-core";

const KIND_COLORS: Record<string, string> = {
  tool: "#0f766e",
  script: "#7c3aed",
  model: "#db2777",
  model_start: "#db2777",
  llm_turn: "#0369a1",
  delegate: "#b45309",
  delegate_parallel: "#b45309",
  web_ingest: "#059669",
  start: "#64748b",
  plan: "#64748b",
  complete: "#64748b",
  error: "#b91c1c",
};

const MODEL_KINDS = new Set(["llm_turn", "model", "model_start"]);

function stepStartMs(step: ParsedMissionStep): number {
  return new Date(step.startedAt ?? step.createdAt).getTime();
}

function stepDurationMs(step: ParsedMissionStep): number {
  if (step.durationMs != null && step.durationMs > 0) return step.durationMs;
  return 0;
}

function barColor(kind: string): string {
  return KIND_COLORS[kind] ?? "#94a3b8";
}

function formatStepCost(step: ParsedMissionStep): string {
  if (step.chargedUsdMicros != null && step.chargedUsdMicros > 0) {
    return formatUsdMicros(step.chargedUsdMicros);
  }
  return "—";
}

function stepLabel(
  step: ParsedMissionStep,
  agentName?: string | null,
  stepModels?: Map<number, string>,
): string {
  const modelId = resolvedModelId(step, stepModels);
  const modelShort = modelId ? formatModelDisplay(modelId).short : null;
  const kindLabel = KIND_LABELS[step.kind] ?? step.kind;
  const base =
    MODEL_KINDS.has(step.kind) && modelShort
      ? `${kindLabel} · ${modelShort}`
      : MODEL_KINDS.has(step.kind)
        ? kindLabel
        : `${kindLabel}${step.summary ? ` · ${step.summary.slice(0, 24)}` : ""}`;
  const agentPrefix = agentName ? `[${agentName}] ` : "";
  return `#${step.seq} ${agentPrefix}${base}`;
}

export function MissionTimeline({
  steps,
  onSelect,
  selectedSeq,
  stepAgents,
  stepModels,
}: {
  steps: ParsedMissionStep[];
  onSelect?: (step: ParsedMissionStep) => void;
  selectedSeq?: number | null;
  stepAgents?: Map<number, string>;
  stepModels?: Map<number, string>;
}) {
  const flat = timelineSteps(steps);
  if (flat.length === 0) return <p className="muted">Nessuno step da visualizzare.</p>;

  const starts = flat.map(stepStartMs);
  const ends = flat.map((s, i) => starts[i] + stepDurationMs(s));
  const t0 = Math.min(...starts);
  const tEnd = Math.max(...ends, t0 + 1);
  const span = Math.max(tEnd - t0, 1);

  const tickCount = 6;
  const ticks: number[] = [];
  for (let i = 0; i <= tickCount; i += 1) {
    ticks.push((span / tickCount) * i);
  }

  const rowHeight = 28;
  const labelWidth = 300;
  const chartWidth = 640;
  const height = flat.length * rowHeight + 36;
  const minBarPx = 3;

  const handleSelect = (step: ParsedMissionStep) => {
    if (!onSelect) return;
    onSelect(step);
  };

  const handleKeyDown = (event: KeyboardEvent, step: ParsedMissionStep) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleSelect(step);
    }
  };

  return (
    <div className="mission-timeline-wrap">
      <svg
        className="mission-timeline-svg"
        viewBox={`0 0 ${labelWidth + chartWidth} ${height}`}
        role="img"
        aria-label="Timeline missione"
      >
        {ticks.map((offset) => {
          const x = labelWidth + (offset / span) * chartWidth;
          return (
            <g key={offset}>
              <line x1={x} y1={0} x2={x} y2={height - 8} stroke="#e2e8f0" strokeWidth={1} />
              <text x={x} y={height - 2} textAnchor="middle" fontSize={10} fill="#64748b">
                {formatMs(offset)}
              </text>
            </g>
          );
        })}

        {flat.map((step, index) => {
          const y = index * rowHeight + 4;
          const start = stepStartMs(step);
          const dur = stepDurationMs(step);
          const leftPct = (start - t0) / span;
          const widthPct = dur > 0 ? Math.max(dur / span, minBarPx / chartWidth) : minBarPx / chartWidth;
          const x = labelWidth + leftPct * chartWidth;
          const w = Math.max(widthPct * chartWidth, minBarPx);
          const indent = step.parentSeq != null ? 12 : 0;
          const agentName = stepAgents?.get(step.seq);
          const modelId = resolvedModelId(step, stepModels);
          const label = stepLabel(step, agentName, stepModels);
          const isSelected = selectedSeq != null && step.seq === selectedSeq;
          const tooltip = [
            agentName ? `agente: ${agentName}` : null,
            step.summary ?? step.kind,
            modelId ? formatModelDisplay(modelId).full : null,
            step.skillId ? `skill: ${step.skillId}` : null,
            step.startedAt ? `inizio: ${formatDateTime(step.startedAt)}` : null,
            stepEndIso(step) ? `fine: ${formatDateTime(stepEndIso(step))}` : null,
            `durata: ${formatMs(dur || null)}`,
            `costo: ${formatStepCost(step)}`,
          ]
            .filter(Boolean)
            .join(" · ");

          return (
            <g
              key={step.seq}
              className={`mission-timeline-row${isSelected ? " mission-timeline-row--selected" : ""}`}
              role="button"
              tabIndex={onSelect ? 0 : undefined}
              style={{ cursor: onSelect ? "pointer" : undefined }}
              onClick={() => handleSelect(step)}
              onKeyDown={(e) => handleKeyDown(e, step)}
              aria-pressed={isSelected}
              aria-label={label}
            >
              <text
                x={8 + indent}
                y={y + 16}
                fontSize={11}
                fill={isSelected ? "#0f766e" : "#334155"}
                fontWeight={isSelected ? 600 : 400}
                className="mission-timeline-label"
              >
                {label.length > 44 ? `${label.slice(0, 41)}…` : label}
              </text>
              <title>{tooltip}</title>
              <rect
                x={x}
                y={y + 6}
                width={w}
                height={14}
                rx={3}
                fill={barColor(step.kind)}
                opacity={isSelected ? 1 : dur > 0 ? 0.85 : 0.45}
                stroke={isSelected ? "#0f766e" : "none"}
                strokeWidth={isSelected ? 2 : 0}
              >
                <title>{tooltip}</title>
              </rect>
              {dur === 0 && (
                <line
                  x1={x}
                  y1={y + 6}
                  x2={x}
                  y2={y + 20}
                  stroke={barColor(step.kind)}
                  strokeWidth={isSelected ? 3 : 2}
                />
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}
